import CreatePage from '@/modules/users/pages/CreatePage.vue'
import EditPage from '@/modules/users/pages/EditPage.vue'
import ShowPage from '@/modules/users/pages/ShowPage.vue'
import ApprovalsPage from '../pages/ApprovalsPage.vue'
import IndexPage from '../pages/IndexPage.vue'

import WaitUntilQuery from '@/components/WaitUntilQuery.vue'

import useAuthUser from '@/composables/useAuthUser'
import useHasPermissionUser from '@/composables/useHasPermissionUser'
import EditApprovalPage from '@/modules/users/pages/EditApprovalPage.vue'
import UserPermissionsPage from '@/modules/users/pages/UserPermissionsPage.vue'
import {
	useRoute,
	type NavigationGuardWithThis,
	type RouteRecordRaw,
} from 'vue-router'

/**
 * Quién puede abrir un perfil.
 *
 * Extraído a una constante porque desde ENL-54 son DOS rutas las que pintan
 * `ShowPage` —`/users/:id` y `/users/:id/feedback/:feedbackId?`— y una copia
 * del guard es una copia que alguien va a olvidar actualizar. Que la ruta nueva
 * naciera sin control de acceso sería la peor forma de estrenarla.
 */
const canOpenProfile: NavigationGuardWithThis<undefined> = (to, from, next) => {
	const { hasPermissionUser } = useHasPermissionUser()
	const { user } = useAuthUser()

	const isOwnProfile = to.params.id?.toString() === user.value?.id?.toString()
	const canViewUsers = hasPermissionUser('view-users', 'App\\Models\\Company')

	if (!user.value?.internal && (canViewUsers || isOwnProfile)) {
		return next()
	} else {
		return next({ path: '/error/not-found' })
	}
}

export default {
	path: '/users',
	children: [
		{
			path: '',
			name: 'users.index',
			component: IndexPage,
			beforeEnter: (to, from, next) => {
				const { user } = useAuthUser()

				if (user.value?.internal) {
					return next({
						name: 'users.index.internal',
					})
				} else {
					return next()
				}
			},
		},
		{
			path: ':id',
			name: 'users.show',
			component: ShowPage,
			beforeEnter: canOpenProfile,
		},
		{
			/*
			 * El reporte pertenece al usuario, y la URL lo dice (ENL-54). Antes
			 * era `/users/838?tab=feedback&feedback=29`: dos query params
			 * sosteniendo lo que es jerarquía.
			 *
			 * `:feedbackId?` opcional para que `/users/838/feedback` abra la
			 * pestaña sin expandir nada.
			 *
			 * Las URLs viejas NO se redirigen: siguen entrando por `users.show`,
			 * que sigue leyendo `?tab` y `?feedback`. Hay campanas ya emitidas en
			 * la base con ese formato y una notificación no se puede reescribir
			 * después de mandada.
			 */
			path: ':id/feedback/:feedbackId?',
			name: 'users.show.feedback',
			component: ShowPage,
			beforeEnter: canOpenProfile,
		},
		{
			path: 'create',
			name: 'users.create',
			component: CreatePage,
			beforeEnter: (to, from, next) => {
				const { hasPermissionUser } = useHasPermissionUser()
				const { user } = useAuthUser()

				if (
					hasPermissionUser('create-users', 'App\\Models\\Company') &&
					!user.value?.internal
				) {
					return next()
				} else {
					return next({
						path: '/error/not-found',
					})
				}
			},
		},
		{
			path: 'edit/:id',
			name: 'users.edit',
			component: EditPage,
			beforeEnter: (to, from, next) => {
				const { hasPermissionUser } = useHasPermissionUser()
				const { user: userLogged } = useAuthUser()
				const router = useRoute()

				next()

				// if (
				//   hasPermissionUser("update-users", "App\\Models\\Company") &&
				//   !userLogged.value?.internal
				// ) {
				//   return next();
				// } else {
				//   return next({
				//     path: "/error/not-found",
				//   });
				// }
			},
		},
		{
			path: 'permissions/:id',
			name: 'users.permissions',
			component: UserPermissionsPage,
			beforeEnter: (to, from, next) => {
				const { hasPermissionUser } = useHasPermissionUser()
				const { user } = useAuthUser()

				if (
					hasPermissionUser('update-users', 'App\\Models\\Company') &&
					!user.value?.internal
				) {
					return next()
				} else {
					return next({
						path: '/error/not-found',
					})
				}
			},
		},
		{
			path: 'approvals',
			children: [
				{
					path: '',
					name: 'users.approvals',
					component: ApprovalsPage,
					// props: {
					//   component: ApprovalsPage,
					// },
				},
				{
					path: ':id',
					name: 'approval.edit',
					component: EditApprovalPage,
				},
			],
		},
	],
} as RouteRecordRaw
